/**
 * UI-layer Asoebi types — snake_case fields matching the kal-shape API
 * (ans-backend routes/planning/asoebiKal.ts). Money is an exact decimal
 * STRING, never a float; every money-bearing row carries its own `currency`
 * (the project's, or null before a price/payment exists). Archive is
 * `archived_at` + the derived `archived` boolean, never a bare flag.
 */

export type StockMovementReason = 'received' | 'sold' | 'returned' | 'damaged' | 'adjustment' | 'initial';

export const STOCK_REASON_LABELS: Record<StockMovementReason, string> = {
  received: 'Received from supplier',
  sold: 'Sold / Assigned to guest',
  returned: 'Returned',
  damaged: 'Damaged / Write-off',
  adjustment: 'Manual adjustment',
  initial: 'Initial stock',
};

export interface StockMovement {
  id: string;
  quantity: number;
  reason: StockMovementReason;
  note: string;
  created_at: string;
}

/** A fabric in the catalogue (items table, item_type='fabric', style='asoebi'). */
export interface Fabric {
  id: string;
  name: string;
  color: string | null;
  /** Exact decimal string; '0' for a free item. */
  price: string;
  /** The project's currency once a price is set; null for a free fabric. */
  currency: string | null;
  stock: number;
  image: string | null;
  category: 'lace' | 'ankara' | 'velvet' | 'silk' | 'organza' | 'adire' | 'accessory' | 'other' | string;
  description: string | null;
  material?: string | null;
  quality?: string | null;
  length?: string | null;
  pattern?: string | null;
  width?: string | null;
  weight_gsm?: string | null;
  care_instructions?: string | null;
  is_active: boolean;
  /** Whether this fabric appears in the guest-facing shop. */
  published_to_shop: boolean;
  archived_at: string | null;
  archived: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * The read vocabulary (deriveAsoebiStatus). Write only through
 * SettableFabricAssignmentStatus — 'pending_confirmation' and 'paid' are never
 * accepted by a PUT; the server rejects them (400).
 */
export type FabricAssignmentStatus =
  | 'pending_confirmation' | 'ordered' | 'paid' | 'delivered' | 'collected' | 'cancelled' | 'returned';
export type SettableFabricAssignmentStatus = 'ordered' | 'delivered' | 'collected' | 'cancelled' | 'returned';

export type PaymentStatus = 'unpaid' | 'partial' | 'paid';
export type AssignmentDeliveryMethod = 'pickup' | 'fixed' | 'courier';
export type FabricKind = 'bundle' | 'product';

export interface StatusTransition {
  status: SettableFabricAssignmentStatus;
  severity: 'info' | 'critical';
}

/**
 * A guest's fabric order (what the API calls an "order"/"assignment" — the
 * sidebar's Orders screen). Checkout-shaped portal orders are listed inside
 * the same array with `read_only: true`.
 */
export interface FabricAssignment {
  id: string;
  project_id?: string;
  guest_id: string | null;
  guest_name: string | null;
  guest_phone: string | null;
  fabric_id: string | null;
  /** Which table fabric_id names. Defaults to 'bundle' server-side. */
  fabric_kind?: FabricKind;
  /** Exact decimal strings. */
  yards: string;
  price_per_yard: string;
  total_price: string;
  currency: string | null;
  status: FabricAssignmentStatus;
  /** True for a portal-checkout row: shown, never editable through this screen. */
  read_only: boolean;
  allowed_next_statuses: SettableFabricAssignmentStatus[];
  allowed_next_transitions: StatusTransition[];
  delivery_date: string | null;
  notes: string | null;
  archived_at: string | null;
  archived: boolean;
  created_at: string;
  updated_at: string;
  payment_status: PaymentStatus;
  payment_amount: string | null;
  payment_method: string | null;
  payment_reference: string | null;
  payment_date: string | null;
  payment_issues: string[];
  /** Not yet returned by GET asoebi/orders — kept optional for a future enrichment; a reader without it just shows nothing. */
  linked_deliveries?: LinkedDeliverySummary[];
}

export type AsoebiOrder = FabricAssignment;

/** One delivery this order is linked to, via fulfillment_lines — not yet
 *  returned inline by GET asoebi/orders (see AsoebiDeliveryService's
 *  getDeliveryLines for the per-delivery view); kept optional so a reader
 *  does not have to special-case its absence. */
export interface LinkedDeliverySummary {
  id: string;
  status: string;
  delivery_method?: string | null;
  zone?: string | null;
  tracking_number?: string | null;
  quantity?: number;
}

export interface FabricAssignmentCreate {
  guest_id: string;
  fabric_id?: string | null;
  yards?: string;
  price_per_yard?: string;
  status?: string | null;
  delivery_date?: string | null;
  notes?: string | null;
}
export type FabricAssignmentUpdate = Partial<FabricAssignmentCreate>;

export interface PaymentInput {
  payment_amount: string;
  payment_method?: string | null;
  payment_reference?: string | null;
  payment_date?: string | null;
  idempotency_key?: string;
}
export interface BatchPaymentInput {
  assignments: { id: string; amount: string }[];
  date?: string | null;
  method?: string | null;
  reference?: string | null;
  paid_by?: string | null;
  notes?: string | null;
}

/** A delivery (its own one-line order, attrs.domain='asoebi_delivery'). */
export interface AsoebiDelivery {
  id: string;
  order_id: string | null;
  guest_name: string;
  guest_phone: string | null;
  fabric_name: string | null;
  delivery_method: AssignmentDeliveryMethod;
  zone: string | null;
  /** Exact decimal string. */
  fee: string;
  currency: string | null;
  tracking_number: string | null;
  status: 'pending' | 'packed' | 'dispatched' | 'delivered' | 'returned' | 'cancelled';
  delivery_address: string | null;
  assigned_to: string | null;
  notes: string | null;
  archived_at: string | null;
  archived: boolean;
  created_at: string;
  updated_at: string;
}
export interface AsoebiDeliveryCreate {
  order_id?: string | null;
  guest_name: string;
  guest_phone?: string | null;
  fabric_name?: string | null;
  delivery_method?: AssignmentDeliveryMethod | null;
  zone?: string | null;
  fee?: string;
  tracking_number?: string | null;
  status?: string | null;
  delivery_address?: string | null;
  assigned_to?: string | null;
  notes?: string | null;
}
export type AsoebiDeliveryUpdate = Partial<AsoebiDeliveryCreate>;

export interface ZoneFee {
  id: string;
  zone_key: string;
  zone_label: string;
  fee: string;
  currency: string | null;
  sort_order: number;
}

/** A guest option for the order/delivery picker, read straight from the guests table. */
export interface GuestOption {
  id: string;
  guest_number?: number;
  name: string;
  phone: string | null;
  email?: string | null;
  archived_at: string | null;
  archived: boolean;
}

/** UI-only — not in shared schema. */
export interface GuestAsoebiProfile {
  guest_id: string;
  assignments: FabricAssignment[];
  /** Exact decimal strings, summed with lib/decimal.ts — never Number(). */
  total_paid: string;
  total_owed: string;
  payment_status: 'fully-paid' | 'partially-paid' | 'unpaid';
  delivery_address: string;
  created_at: string;
  updated_at: string;
}

export interface AsoebiOverview {
  orders_count: number;
  currency: string | null;
  orders_total: string | null;
  orders_paid: string | null;
  orders_by_currency: { currency: string | null; orders_count: number; total: string; paid: string }[];
  assignments_count: number;
  pending_deliveries: number;
}

export interface AsoebiStats {
  totalFabrics: number;
  activeFabrics: number;
  totalAssignments: number;
  totalRevenue: string | null;
  pendingDeliveries: number;
  paidAssignments: number;
  pendingConfirmations: number;
  currency: string | null;
}

export interface AsoebiFilter {
  category?: string;
  status?: string;
  searchTerm?: string;
  guestId?: string;
  /** Show archived rows instead of active ones. */
  archived?: boolean;
}

/** A fabric line in a bundle. */
export interface BundleItem {
  fabricId: string;
  yards: number;
}
export type ExtraBundleItemCategory = 'favour' | 'delivery' | 'other';
export interface ExtraBundleItem {
  id: string;
  category: ExtraBundleItemCategory;
  name: string;
  quantity: number;
  unit: string;
}
export interface FabricBundle {
  id: string;
  name: string;
  description?: string;
  image?: string;
  items: BundleItem[];
  extraItems?: ExtraBundleItem[];
  price: number;
  eventIds?: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}
